import { useState } from 'react';
import { SMSNumberV1 } from '@frontend/api-sms-number';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber, formatPhoneNumberE164 } from '@frontend/phone-numbers';
import type { PersonPhoneSelection, ThreadHeaderSize } from '@frontend/thread-header-context';
import type { CommonHTMLAttributes } from '@frontend/types';
import { theme } from '@frontend/theme';
import { Button, PopoverMenu, PopoverMenuItem, styles, Text, usePopoverMenu } from '@frontend/design-system';
import { CustomPhoneInputField } from './custom-phone-input-field';
import { PersonPhoneSelectorItem } from './person-phone-selector-item';

export type PersonPhoneSelectorState = {
  showButton: boolean;
  currentSelectedPhone: SMSNumberV1.Types.PhoneValidity;
  currentSelectedPhoneLabel: string;
  validPhones: SMSNumberV1.Types.PhoneValidity[];
  allPhones: SMSNumberV1.Types.PhoneValidity[];
  isCurrentSelectedNumberCustom?: boolean;
  allowCustomPhoneNumber?: boolean;
  popoverState: ReturnType<typeof usePopoverMenu<HTMLElement>>;
  locationId: string;
  personId?: string;
};

export type PersonPhoneSelectorProps = PersonPhoneSelectorState & {
  color?: 'default' | 'white';
  onSelectPersonPhone: (selection: PersonPhoneSelection, threadId?: string) => void;
  variant?: ThreadHeaderSize;
} & CommonHTMLAttributes;

export const PersonPhoneSelector = ({
  showButton,
  currentSelectedPhone,
  currentSelectedPhoneLabel,
  validPhones,
  allPhones: _,
  popoverState,
  color = 'default',
  onSelectPersonPhone,
  isCurrentSelectedNumberCustom,
  allowCustomPhoneNumber,
  locationId,
  variant = 'normal',
  personId,
  ...rest
}: PersonPhoneSelectorProps) => {
  const { t } = useTranslation('thread-header');

  const [showCustomPhoneInput, setShowCustomPhoneInput] = useState<boolean>(!!isCurrentSelectedNumberCustom);

  const showPhoneDropdownButton = (showButton || allowCustomPhoneNumber) && !!validPhones.length;

  const handleCustomNumberChange = (newCustomNumber: string) => {
    const isNewCustomNumberValid = newCustomNumber.length === 10;
    if (currentSelectedPhone.number !== newCustomNumber)
      onSelectPersonPhone({ personPhone: isNewCustomNumberValid ? newCustomNumber : '', personId });
  };

  const formattedPhoneNumber = formatPhoneNumber(currentSelectedPhone.number);

  return (
    <>
      <span
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(0.25),
          justifyContent: 'flex-start',
          overflow: 'hidden',
        }}
        {...rest}
      >
        {variant === 'normal' && (
          <Text color='subdued' size='small' className='to-text' weight={color === 'white' ? 'bold' : undefined}>
            {t('To:')}
          </Text>
        )}
        {showCustomPhoneInput ? (
          <>
            <Icon name='cell-phone-small' color='subdued' height={12} width={12} css={{ fontWeight: 'bold' }} />
            <CustomPhoneInputField
              onCustomNumberChange={handleCustomNumberChange}
              initValue={isCurrentSelectedNumberCustom ? currentSelectedPhone.number : ''}
              iconColor={color}
              popoverState={popoverState}
            />
          </>
        ) : showPhoneDropdownButton ? (
          <Button
            variant='tertiary'
            css={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(0.5),
              color: color === 'white' ? theme.colors.white : theme.colors.primary50,
              fontWeight: 'bold',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              height: 24,
              padding: theme.spacing(0.375, 0.25),
              '&:hover, &:active:not([aria-disabled="true"])': {
                background: theme.colors.neutral5,
              },
            }}
            hoverLabel={`${currentSelectedPhoneLabel} ${formattedPhoneNumber}`.trim()}
            {...popoverState.getTriggerProps()}
          >
            <Icon
              name='cell-phone-small'
              color={color === 'white' ? 'white' : 'subdued'}
              height={12}
              width={12}
              css={{ fontWeight: 'bold' }}
            />
            <Text size='small' weight='bold' css={styles.truncate}>
              {currentSelectedPhoneLabel && <span className='to-label'>{currentSelectedPhoneLabel}&nbsp;</span>}
              <span className='to-phone'>{formattedPhoneNumber}</span>
            </Text>
            <Icon
              name='caret-down-tiny'
              className='caret-icon'
              color={color === 'white' ? 'white' : 'light'}
              css={[
                { transition: 'rotate 200ms ease-in-out' },
                popoverState.isOpen && { rotate: '-180deg' },
                { minWidth: 'min-content' },
              ]}
              height={8}
              width={8}
              fontWeight={800}
            />
          </Button>
        ) : (
          <>
            <Icon name='cell-phone-small' color='subdued' height={12} width={12} css={{ fontWeight: 'bold' }} />
            <Text
              size='small'
              color='light'
              weight='bold'
              css={{ lineHeight: 1, padding: theme.spacing(1, 0.5), ...styles.truncate }}
            >
              {currentSelectedPhoneLabel && <span className='to-label'>{currentSelectedPhoneLabel}&nbsp;</span>}
              <span className='to-phone'>{formattedPhoneNumber}</span>
            </Text>
          </>
        )}
      </span>
      {showPhoneDropdownButton && (
        <PopoverMenu {...popoverState.getMenuProps()}>
          {validPhones.map(({ number, label }, index) => {
            const active = formatPhoneNumberE164(number) === formatPhoneNumberE164(currentSelectedPhone.number);
            return (
              <PersonPhoneSelectorItem
                key={`${label}-${number}-${index}`}
                {...popoverState.getItemProps({
                  index,
                  onClick: () => {
                    setShowCustomPhoneInput(false);
                    onSelectPersonPhone({ personPhone: number, personId });
                  },
                })}
                active={active}
                personPhone={number}
                phoneLabel={label}
                locationId={locationId}
                data-testid='person-phone-selector-item'
              />
            );
          })}
          {allowCustomPhoneNumber && (
            <>
              {/* TODO: replace hardcoded value */}
              <Divider margin={0} css={{ width: showCustomPhoneInput ? '218px' : 'auto' }} />
              <PopoverMenuItem
                key='custom-phone'
                {...popoverState.getItemProps({
                  index: validPhones.length,
                  onClick: () => setShowCustomPhoneInput(true),
                })}
                active={showCustomPhoneInput}
              >
                <Text color='primary'> {t('Custom Number')} </Text>
              </PopoverMenuItem>
            </>
          )}
        </PopoverMenu>
      )}
    </>
  );
};
